"use client";

import Link from "next/link";
import { FEATURED_WORK } from "@/constants/home.constants";
import { useFeaturedWorkCarousel } from "@/hooks/useFeaturedWorkCarousel";

const SLIDE_GRADIENTS = [
  "from-[#0b1a3e] via-[#0d2257] to-[#091530]",
  "from-[#0a2040] via-[#0c2d5e] to-[#071428]",
  "from-[#061a36] via-[#0a2850] to-[#050f20]",
  "from-[#0c1e44] via-[#0f2a64] to-[#081225]",
];

export default function FeaturedWorkSection() {
  const { active, setActive, prev, next, total } = useFeaturedWorkCarousel();
  const item = FEATURED_WORK[active];

  return (
    <section id="portfolio" className="reveal-section space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[var(--accent)]">Featured Work</p>
          <h2 className="mt-3 text-4xl md:text-5xl">Production systems we&apos;re proud of.</h2>
        </div>
        <Link
          href="/portfolio"
          className="hidden text-sm font-semibold text-[var(--accent)] transition hover:opacity-70 md:block"
        >
          See all projects →
        </Link>
      </div>

      {/*
        Carousel wrapper.
        The arrows live OUTSIDE the slide (on either side of the rounded
        card) so they don't overlap the image. We pad the wrapper laterally
        and absolutely-position the buttons in the outer gutter — on small
        screens (where there's no room) the buttons drop to inside the slide
        edge via `inset-x-2` and `md:-inset-x-5`.
      */}
      <div className="relative px-2 md:px-14">
        <div className="overflow-hidden rounded-2xl border border-[var(--stroke)]">
          <div
            className={`relative flex h-80 items-end p-8 transition-all duration-500 md:h-96 ${item.image ? "bg-slate-900" : `bg-gradient-to-br ${SLIDE_GRADIENTS[active]}`}`}
            style={item.image ? {
              backgroundImage: `url("${item.image}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            } : undefined}
          >
            {/* Without a photo we keep the neural grid + accent washes;
                with a photo we use a darker bottom gradient so the title
                and blurb remain readable across any frame. */}
            {item.image ? (
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
            ) : (
              <div className="neural-bg opacity-10" />
            )}

            <div className="relative space-y-1">
              <p className="text-xs uppercase tracking-[0.2em] text-[var(--accent)]">{item.meta}</p>
              <h3 className="text-2xl md:text-3xl">{item.title}</h3>
              <p className="mt-2 max-w-lg text-sm text-[var(--muted)]">{item.blurb}</p>
              <Link
                href="/portfolio"
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[var(--accent)] transition hover:gap-2"
              >
                View case study →
              </Link>
            </div>

            <div className="absolute bottom-5 right-8 flex gap-2">
              {FEATURED_WORK.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-label={`Show slide ${i + 1}`}
                  aria-current={active === i}
                  className="group relative inline-flex h-9 w-9 items-center justify-center"
                >
                  <span
                    className={`block h-1.5 rounded-full transition-all duration-300 ${
                      active === i ? "w-6 bg-[var(--accent)]" : "w-1.5 bg-white/30 group-hover:bg-white/60"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Outside arrows — sit in the gutter on md+, on the slide edge on mobile */}
        <button
          onClick={prev}
          aria-label="Previous slide"
          className="absolute left-0 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--stroke)] bg-[var(--panel)]/95 text-lg text-[var(--text)] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] backdrop-blur transition hover:scale-105 hover:border-[var(--accent)] hover:text-[var(--accent)] md:left-2"
        >
          ‹
        </button>
        <button
          onClick={next}
          aria-label="Next slide"
          className="absolute right-0 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--stroke)] bg-[var(--panel)]/95 text-lg text-[var(--text)] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] backdrop-blur transition hover:scale-105 hover:border-[var(--accent)] hover:text-[var(--accent)] md:right-2"
        >
          ›
        </button>
      </div>

      <Link
        href="/portfolio"
        className="mt-1 block text-center text-sm font-semibold text-[var(--accent)] transition hover:opacity-70 md:hidden"
      >
        See all projects →
      </Link>
    </section>
  );
}
